import { useState } from "react";
import { Link } from "react-router-dom";
import { useMoneyAccounts } from "@/api/cash";
import {
  useBuckets,
  useRecordExpense,
  useSaveSplit,
  useSplit,
  type Bucket,
  type InvoiceSplit,
  type SplitRow,
} from "@/api/finance";
import { ExpenseForm } from "@/components/forms/ExpenseForm";
import { SplitEditor, type SplitDraft } from "@/components/invoices/SplitEditor";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMoney } from "@/lib/format";
import { centsToAmount } from "@/lib/money";

type Mode = { kind: "edit" } | { kind: "payout"; row: SplitRow } | null;

function cents(amount: string): number {
  return Math.round(Number(amount) * 100);
}

/** What the client has paid towards this part and nobody has been paid yet. */
function owedNow(row: SplitRow): string {
  const owed = cents(row.received_share) - cents(row.paid_out);
  return owed > 0 ? centsToAmount(owed) : "";
}

/** The invoice's rows, then every other active bucket with no share yet. */
function drafts(split: InvoiceSplit, buckets: Bucket[]): SplitDraft[] {
  const listed: SplitDraft[] = split.rows.map(({ bucket_id, bucket_name, percent, payee }) => ({
    bucket_id,
    bucket_name,
    percent,
    payee,
  }));
  for (const bucket of buckets) {
    if (!split.rows.some((row) => row.bucket_id === bucket.id)) {
      listed.push({
        bucket_id: bucket.id,
        bucket_name: bucket.name,
        percent: "",
        payee: bucket.default_payee,
      });
    }
  }
  return listed;
}

export function SplitCard({ invoiceId }: { invoiceId: string }) {
  const split = useSplit(invoiceId);
  const buckets = useBuckets();
  const moneyAccounts = useMoneyAccounts();
  const save = useSaveSplit(invoiceId);
  const record = useRecordExpense();
  const [mode, setMode] = useState<Mode>(null);
  const data = split.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Where the money goes</CardTitle>
        <CardDescription>
          {data?.saved
            ? "This invoice's own split."
            : "Following the bucket defaults in Settings. Edit to change it for this invoice only."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {split.isPending || buckets.isPending ? (
          <p className="text-sm text-muted-foreground">Loading the split…</p>
        ) : split.isError || buckets.isError ? (
          <RequestError error={split.error ?? buckets.error} />
        ) : mode?.kind === "edit" ? (
          <SplitEditor
            rows={drafts(split.data, buckets.data)}
            onSave={async (rows) => {
              await save.mutateAsync(rows);
              setMode(null);
            }}
            onCancel={() => setMode(null)}
          />
        ) : mode?.kind === "payout" ? (
          <ExpenseForm
            buckets={split.data.rows.map((row) => ({ id: row.bucket_id, name: row.bucket_name }))}
            accounts={moneyAccounts.data}
            payout={{
              invoiceId,
              invoiceNo: split.data.invoice_no,
              currency: split.data.currency,
            }}
            initialBucketId={mode.row.bucket_id}
            initialPayee={mode.row.payee}
            initialAmount={owedNow(mode.row)}
            submitLabel="Record payout"
            onSubmit={async (input) => {
              await record.mutateAsync(input);
              setMode(null);
            }}
            onCancel={() => setMode(null)}
          />
        ) : (
          <>
            {!split.data.saved && split.data.total_percent !== "100.00" && (
              <Alert
                variant="warning"
                title={`The bucket defaults add up to ${Number(split.data.total_percent)}%`}
              >
                This invoice follows them, so part of it is not allocated. Fix the defaults in
                Settings, or edit this invoice's split.
              </Alert>
            )}
            <SplitTable split={split.data} onPayout={(row) => setMode({ kind: "payout", row })} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setMode({ kind: "edit" })}>
                Edit split
              </Button>
              <Link
                to={`/expenses?invoice=${invoiceId}`}
                className="inline-flex items-center px-2 text-sm text-primary hover:underline"
              >
                Payouts and expenses for this invoice
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SplitTable({
  split,
  onPayout,
}: {
  split: InvoiceSplit;
  onPayout: (row: SplitRow) => void;
}) {
  const money = (amount: string) => formatMoney(amount, split.currency);
  return (
    <>
      <ul className="flex flex-col divide-y md:hidden">
        {split.rows.map((row) => (
          <li key={row.bucket_id} className="flex items-start justify-between gap-3 py-3">
            <div className="min-w-0 text-sm">
              <p className="font-medium">
                {row.bucket_name} · {Number(row.percent)}%
              </p>
              {row.payee && <p className="text-xs text-muted-foreground">{row.payee}</p>}
              <p className="text-xs text-muted-foreground">
                Planned {money(row.planned)} · paid out {money(row.paid_out)}
              </p>
              <p>
                Left {money(row.balance)}
                {row.kind === "payout" && owedNow(row) && (
                  <Badge variant="warning" className="ml-2">
                    Owed {money(owedNow(row))}
                  </Badge>
                )}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onPayout(row)}>
              Record payout
            </Button>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-2 pr-3 font-medium">Bucket</th>
              <th className="py-2 pr-3 text-right font-medium">Share</th>
              <th className="py-2 pr-3 text-right font-medium">Planned</th>
              <th className="py-2 pr-3 text-right font-medium">Of what came in</th>
              <th className="py-2 pr-3 text-right font-medium">Paid out</th>
              <th className="py-2 pr-3 text-right font-medium">Left</th>
              <th className="py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {split.rows.map((row) => {
              const owed = row.kind === "payout" && owedNow(row) !== "";
              return (
                <tr key={row.bucket_id} className="border-b last:border-0">
                  <td className="py-2 pr-3">
                    <span className="font-medium">{row.bucket_name}</span>
                    {row.payee && (
                      <span className="block text-xs text-muted-foreground">{row.payee}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(row.percent)}%</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                    {money(row.planned)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                    {money(row.received_share)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                    {Number(row.paid_out) > 0 ? money(row.paid_out) : "—"}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                    {money(row.balance)}
                    {owed && (
                      <Badge variant="warning" className="ml-2">
                        Owed {money(owedNow(row))}
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onPayout(row)}>
                      Record payout
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
